/**
 * Client admin de la newsletter : envoi du digest (edge fn) + stats abonnés.
 */

import { supabase } from '@/lib/supabase';
import type { NewsletterStatus, NewsletterSubscriberSummary } from '@jeraisonne/db';

export class NewsletterError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'NewsletterError';
  }
}

export interface DigestResult {
  ok: boolean;
  sent: number;
  failed: number;
  articles: number;
  recipients: number;
}

export async function sendNewsletterDigest(opts: { limit?: number } = {}): Promise<DigestResult> {
  const { data, error } = await supabase.functions.invoke<DigestResult>('newsletter-send-digest', {
    body: { ...(opts.limit ? { limit: opts.limit } : {}) },
  });
  if (error) {
    let message = error.message;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        /* garde le message générique */
      }
    }
    throw new NewsletterError(message);
  }
  if (!data) throw new NewsletterError('empty_response');
  return data;
}

export type NewsletterStats = Record<NewsletterStatus, number> & { total: number };

export async function fetchNewsletterStats(): Promise<NewsletterStats> {
  const { data, error } = await supabase.from('newsletter_subscribers').select('status');
  if (error) throw new Error(error.message);
  const stats: NewsletterStats = { pending: 0, confirmed: 0, unsubscribed: 0, total: 0 };
  for (const row of (data ?? []) as { status: NewsletterStatus }[]) {
    stats[row.status] = (stats[row.status] ?? 0) + 1;
    stats.total += 1;
  }
  return stats;
}

const SUBSCRIBER_COLS = 'id, email, status, source, confirmed_at, created_at';

/** Liste des abonnés (sans jetons) pour l'administration — lecture admin RLS. */
export async function fetchNewsletterSubscribers(): Promise<NewsletterSubscriberSummary[]> {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select(SUBSCRIBER_COLS)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as NewsletterSubscriberSummary[];
}
